import {
  getUbmPriceElasticityGroup,
  type TopListingPrices,
} from '@smg-real-estate/toplisting-prices';
import { WINCASA_SPECIAL_PRICING_CUSTOMER_IDS } from '@smg-real-estate/toplisting-prices/dist/constants';

// The prices before the Top Listing BUY price change (ASR-1936). `toplisting-prices` only knows the
// current prices, so the old ones are frozen here.
const PRICE_TOP_LISTING_RENT_CHF_CENTS = 1295;
const PRICE_TOP_LISTING_BUY_CHF_CENTS = 1495;
const PRICE_PREMIUM_LISTING_CHF_CENTS = 2500;
const WINCASA_PRICE_TOP_LISTING_RENT_CHF_CENTS = 700;
const WINCASA_PRICE_TOP_LISTING_BUY_CHF_CENTS = 1495;
const WINCASA_PRICE_PREMIUM_LISTING_CHF_CENTS = 2500;
const UBM_PRICE_ELASTICITY_V1_PRICE_TOP_LISTING_BUY_CHF_CENTS = 970;
const UBM_PRICE_ELASTICITY_V2_PRICE_TOP_LISTING_BUY_CHF_CENTS = 1970;

/**
 * Converts a price in cents to CHF.
 * @param chfCents - The price in cents.
 * @returns The price in CHF rounded to two decimal places.
 */
function convertChfCentsToChf(chfCents: number): number {
  return roundToTwoDecimalPlaces(chfCents / 100);
}

/**
 * Gets the daily top listing prices
 * @param customerId - Optional customer identifier for special pricing
 * @returns An object containing the top listing prices
 */
export function getTopListingPrices_OLD(customerId?: string): TopListingPrices {
  let rentPriceCents = PRICE_TOP_LISTING_RENT_CHF_CENTS;
  let buyPriceCents = PRICE_TOP_LISTING_BUY_CHF_CENTS;
  let premiumPriceCents = PRICE_PREMIUM_LISTING_CHF_CENTS;

  // If customerId is in the list of special customer ids, use Wincasa special pricing
  if (customerId && WINCASA_SPECIAL_PRICING_CUSTOMER_IDS.includes(customerId)) {
    rentPriceCents = WINCASA_PRICE_TOP_LISTING_RENT_CHF_CENTS;
    buyPriceCents = WINCASA_PRICE_TOP_LISTING_BUY_CHF_CENTS;
    premiumPriceCents = WINCASA_PRICE_PREMIUM_LISTING_CHF_CENTS;
  }

  return {
    priceTopListingRentChf: convertChfCentsToChf(rentPriceCents),
    priceTopListingRentChfCents: rentPriceCents,
    priceTopListingBuyChf: convertChfCentsToChf(buyPriceCents),
    priceTopListingBuyChfCents: buyPriceCents,
    pricePremiumListingChf: convertChfCentsToChf(premiumPriceCents),
    pricePremiumListingChfCents: premiumPriceCents,
  };
}

/**
 * Gets the Top Listing BUY price of the UBM price elasticity experiment (ASR-1346) before the BUY
 * price change, as `toplisting-prices` v2.3.0 returned it with `{ experiment: 'UBM_PRICE_ELASTICITY' }`.
 * @param customerId - Optional customer identifier for special pricing
 * @returns The BUY price in CHF
 */
export function getUbmPriceElasticityTopListingBuyPriceChf_OLD(customerId?: string): number {
  const isWincasa = !!customerId && WINCASA_SPECIAL_PRICING_CUSTOMER_IDS.includes(customerId);
  const group = isWincasa ? undefined : getUbmPriceElasticityGroup(customerId);

  if (group === 'V1') {
    return convertChfCentsToChf(UBM_PRICE_ELASTICITY_V1_PRICE_TOP_LISTING_BUY_CHF_CENTS);
  }
  if (group === 'V2') {
    return convertChfCentsToChf(UBM_PRICE_ELASTICITY_V2_PRICE_TOP_LISTING_BUY_CHF_CENTS);
  }
  return getTopListingPrices_OLD(customerId).priceTopListingBuyChf;
}

/**
 * Rounds a positive number to two decimal places.
 * @param positiveNumber - The number to round.
 * @returns The number rounded to two decimal places.
 * @throws Will throw an error if the number is negative.
 */
export function roundToTwoDecimalPlaces(positiveNumber: number): number {
  if (positiveNumber === 0) {
    return 0;
  }

  if (positiveNumber < 0) {
    throw new Error('Only positive numbers are allowed');
  }

  return Math.round((positiveNumber + Number.EPSILON) * 100) / 100;
}
